import { useState } from "react";
import type { FormEvent } from "react";
import { site } from "@/mocks/chiagua";

const SUBMIT_URL = "https://readdy.ai/api/form/dagj59bep053ijub2b9g";

type Status = "idle" | "loading" | "success" | "error";

const inputCls =
  "w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setFormError("");

    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt")?.toString() ?? "").trim();

    // 蜜罐命中：不提交，返回与正常提交一致的成功提示，避免被机器人识别
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload: Record<string, string> = {};
    fd.forEach((value, key) => {
      const text = String(value).trim();
      if (key !== "website_alt" && text !== "") {
        payload[key] = text;
      }
    });

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      });
      const responseText = await res.text();

      let code = "";
      let serverMsg = "";
      try {
        const parsed = responseText ? JSON.parse(responseText) : null;
        code = parsed?.code ?? "";
        serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      } catch {
        serverMsg = responseText;
      }

      const ok = res.ok && code === "OK";
      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败了，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络开小差了，请检查后重试。");
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        {/* 左：说明 */}
        <div>
          <p className="font-heading text-2xl text-primary-500 md:text-3xl">📮 投稿 & 联系</p>
          <h2 className="mt-2 text-2xl font-black text-foreground-950 md:text-4xl">有好料，就投给我们</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            你在评论区刷到过能让人笑一整天的神评或名场面？欢迎投喂。审核通过后，会署上你的昵称出现在对应合集里。
          </p>

          <ul className="mt-6 space-y-4 text-sm text-foreground-700">
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-lg text-accent-700">
                <i className="ri-vip-crown-line" aria-hidden="true"></i>
              </span>
              <span>
                <strong className="font-bold">投稿神评 / 名场面</strong>
                <p className="mt-0.5 text-xs text-foreground-500">附上原视频与出处更佳，越"敢说"越好笑越好。</p>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-lg text-secondary-700">
                <i className="ri-image-add-line" aria-hidden="true"></i>
              </span>
              <span>
                <strong className="font-bold">梗图素材 / 商务合作</strong>
                <p className="mt-0.5 text-xs text-foreground-500">高清素材与长期合作，欢迎在表单中留言说明。</p>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-background-300 text-lg text-foreground-800">
                <i className="ri-time-line" aria-hidden="true"></i>
              </span>
              <span>
                <strong className="font-bold">响应时间</strong>
                <p className="mt-0.5 text-xs text-foreground-500">通常 1–3 个工作日内回复，记得留下有效联系方式。</p>
              </span>
            </li>
          </ul>

          <div className="mt-7 flex items-center gap-3 rounded-2xl bg-background-100 p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-lg text-background-50">
              <i className="ri-mail-line" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-xs text-foreground-500">也可直接邮件联系</p>
              <a href={`mailto:${site.mail}`} className="text-sm font-bold text-primary-700 hover:underline">
                {site.mail}
              </a>
            </div>
          </div>
        </div>

        {/* 右：表单 */}
        <form
          id="contact-form"
          data-readdy-form="吃瓜网投稿与留言表单"
          onSubmit={handleSubmit}
          className="rounded-3xl border border-background-200 bg-background-100 p-5 md:p-7"
          noValidate={false}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-foreground-900">你的昵称</span>
              <input type="text" name="nickname" required maxLength={30} placeholder="怎么称呼你" className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-foreground-900">联系方式</span>
              <input type="email" name="email" required placeholder="邮箱或常用账号" className={inputCls} />
            </label>
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-bold text-foreground-900">投稿类型</span>
            <div className="relative">
              <select
                name="topic"
                required
                defaultValue="投稿神评"
                className={`${inputCls} cursor-pointer appearance-none pr-10`}
              >
                <option>投稿神评</option>
                <option>投稿名场面</option>
                <option>梗图素材 / 商务合作</option>
                <option>建议反馈</option>
                <option>其他</option>
              </select>
              <i
                className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xl text-foreground-500"
                aria-hidden="true"
              ></i>
            </div>
          </label>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-bold text-foreground-900">想说的话</span>
            <textarea
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="把那条神评原文、名场面出处或合作意向贴在这里（最多 500 字）……"
              className={`${inputCls} resize-none`}
            ></textarea>
            <span className="mt-1 block text-right text-xs text-foreground-400" aria-hidden="true">最多 500 字</span>
          </label>

          {/* 蜜罐隐藏字段 */}
          <input
            type="text"
            name="website_alt"
            className="website-alt-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <div className="mt-5">
            {status === "success" && (
              <p className="mb-4 flex items-center gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm font-medium text-accent-800">
                <i className="ri-checkbox-circle-fill" aria-hidden="true"></i> 收到啦！我们会尽快处理，感谢你的投喂。
              </p>
            )}
            {status === "error" && formError && (
              <p className="mb-4 flex items-start gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800">
                <i className="ri-alert-line mt-0.5" aria-hidden="true"></i> {formError}
              </p>
            )}
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-base font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i> 正在发送…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill" aria-hidden="true"></i> 一键投稿吃瓜
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}